import React, { useMemo } from 'react';
import classNames from 'classnames';
import styles from './SuggestionAutocompleteTextbox.module.scss';
import { ComboBox, ComboBoxProps } from 'components/core';
import { useSuggestionAutocomplete } from 'concepts/AutoDocExtraction/SuggestionAutocompleteContext';

export const SUGGESTION_AUTOCOMPLETE_HEADING =
  'Items with automated suggestions';

export type SuggestionAutocompleteTextboxProps = Omit<
  ComboBoxProps,
  | 'data'
  | 'allowFreeText'
  | 'hasChevron'
  | 'enableFiltering'
  | 'listHeading'
  | 'noResultsMessage'
> & {
  blockType: string;
};

// The label input of the DE2 block editors: a free-text ComboBox that offers
// the template labels of suggestion-supported fields while the user types.
// Picking one is an onChange with that label, as if it had been typed
export function SuggestionAutocompleteTextbox({
  value,
  blockType,
  className,
  ...comboBoxProps
}: SuggestionAutocompleteTextboxProps): JSX.Element {
  const { findSuggestionOptions } = useSuggestionAutocomplete();

  // The matches for the current label: empty under two characters, for a
  // label that already is an alias, or without a match. An item whose label
  // already partly matches (e.g. "Study") lists them as soon as it gets focus
  const data = useMemo(
    () =>
      findSuggestionOptions(value, blockType).map(
        ({ templateLabel }) => templateLabel
      ),
    [findSuggestionOptions, value, blockType]
  );

  return (
    <div className={styles.field}>
      <ComboBox
        {...comboBoxProps}
        value={value}
        data={data}
        allowFreeText
        hasChevron={false}
        listHeading={SUGGESTION_AUTOCOMPLETE_HEADING}
        className={classNames(styles.labelInput, className)}
      />
    </div>
  );
}
